import { Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider } from './contexts/auth-context'
import { SidebarProvider } from './contexts/sidebar-context'
import { I18nProvider } from './contexts/i18n-context'
import { ErrorBoundary } from './components/error-boundary'
import { Toaster } from './components/ui/toaster'
import { Toaster as SonnerToaster } from './components/ui/sonner'
import { OfflineScreen } from './components/offline-screen'
// import { CookieConsent } from './components/cookie-consent'
// import { useVisitorTracking, useInteractionTracking } from './hooks/use-visitor-tracking'
import Layout from './components/layout'
import { ProtectedRoute } from './components/protected-route'
import HomePage from './pages/home'
import LandingPage from './pages/landing'
import AccommodationPage from './pages/landing/accommodation'
import BarAndGrillPage from './pages/landing/bar-and-grill'
import ToursPage from './pages/landing/tours'
import ContactPage from './pages/landing/contact'
import RestaurantMenuPage from './pages/restaurant-menu'
import Dashboard from './pages/dashboard'
import Accounting from './pages/accounting'
import Sales from './pages/sales'
import Expenses from './pages/expenses'
import PurchaseOrders from './pages/purchase-orders'
import AccountsPayable from './pages/accounts-payable'
import EnhancedAccountsPayable from './pages/enhanced-accounts-payable'
import Banking from './pages/banking'
import Inventory from './pages/inventory'
import Reports from './pages/reports'
import Settings from './pages/settings'
import AIInsights from './pages/ai-insights'
import EnhancedTransactionProcessing from './pages/enhanced-transaction-processing'
import EnhancedJournalManagement from './pages/enhanced-journal-management'
import CreditNotes from './pages/credit-notes'
import PeriodClose from './pages/period-close'
import RevenueRecognition from './pages/revenue-recognition'
import FixedAssets from './pages/fixed-assets'
import LlamaAI from './pages/llama-ai'
import Tax from './pages/tax'
import TaxCalculation from './pages/tax-calculation'
import Clients from './pages/clients'
import Security from './pages/security'
import Companies from './pages/companies'
import Help from './pages/help'
import HelpSupport from './pages/help-support'
import FinancialReports from './pages/financial-reports'
import JournalNew from './pages/journal-new'
import BudgetManagement from './pages/budget-management'
import Documents from './pages/documents'
import POS from './pages/pos'
import JournalHub from './pages/journal-hub'
import UnifiedApprovalHub from './pages/unified-approval-hub'
import SupplierPortal from './pages/supplier-portal'
import VendorsPage from './pages/vendors'
import PayrollPage from './pages/payroll'
import UsersPage from './pages/users'
import Profile from './pages/profile'
import HotelPage from './pages/hotel'
import Login from './pages/auth/login'
import Register from './pages/auth/register'
import ForgotPassword from './pages/auth/forgot-password'
import VerifyEmail from './pages/auth/verify-email'
import Unauthorized from './pages/unauthorized'
import MyAccount from './pages/my-account'
import GuestDashboard from './pages/guest-dashboard'
import FoodInventoryPage from './pages/food-inventory'
import RoomDetailsPage from './pages/room-details'
import Analytics from './pages/analytics'

function AppContent() {
  // Initialize visitor tracking
  // useVisitorTracking();
  // const { trackClick, trackError } = useInteractionTracking();

  return (
    <>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/auth/login" element={<Login />} />
        <Route path="/auth/register" element={<Register />} />
        <Route path="/auth/forgot-password" element={<ForgotPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/unauthorized" element={<Unauthorized />} />
        {/* Landing page routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/accommodation" element={<AccommodationPage />} />
        <Route path="/room/:roomId" element={<RoomDetailsPage />} />
        <Route path="/bar-and-grill" element={<BarAndGrillPage />} />
        <Route path="/restaurant-menu" element={<RestaurantMenuPage />} />
        <Route path="/tours" element={<ToursPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/home" element={<HomePage />} />
        {/* Redirect /reports to /dashboard/reports */}
        <Route path="/reports" element={<Navigate to="/dashboard/reports" replace />} />
        {/* Redirect /documents to /dashboard/documents */}
        <Route path="/documents" element={<Navigate to="/dashboard/documents" replace />} />
        {/* Redirect /banking to /dashboard/banking */}
        <Route path="/banking" element={<Navigate to="/dashboard/banking" replace />} />
        <Route path="/my-account" element={<MyAccount />} />
        <Route path="/guest-dashboard" element={<GuestDashboard />} />
        <Route path="/dashboard" element={
          <ProtectedRoute allowedRoles={['super_admin', 'admin', 'auditor', 'employee', 'manager', 'waiter', 'receptionist']}>
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<Dashboard />} />
          <Route path="accounting" element={<Accounting />} />
          <Route path="sales" element={<Sales />} />
          <Route path="pos" element={<POS />} />
          <Route path="journal-hub" element={<JournalHub />} />
          <Route path="approval-hub" element={<UnifiedApprovalHub />} />
          <Route path="supplier-portal" element={<SupplierPortal />} />
          <Route path="vendors" element={<VendorsPage />} />
          <Route path="payroll" element={<PayrollPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="hotel" element={<HotelPage />} />
          <Route path="expenses" element={<Expenses />} />
          <Route path="purchase-orders" element={<PurchaseOrders />} />
          <Route path="accounts-payable" element={<AccountsPayable />} />
          <Route path="enhanced-accounts-payable" element={<EnhancedAccountsPayable />} />
          <Route path="banking" element={<Banking />} />
          <Route path="inventory" element={<Inventory />} />
          <Route path="food-inventory" element={<FoodInventoryPage />} />
          <Route path="reports" element={<Reports />} />
          <Route path="settings" element={<Settings />} />
          <Route path="profile" element={<Profile />} />
          <Route path="ai-insights" element={<AIInsights />} />
          <Route path="enhanced-transaction-processing" element={<EnhancedTransactionProcessing />} />
          <Route path="enhanced-journal-management" element={<EnhancedJournalManagement />} />
          <Route path="credit-notes" element={<CreditNotes />} />
          <Route path="period-close" element={<PeriodClose />} />
          <Route path="revenue-recognition" element={<RevenueRecognition />} />
          {/* <Route path="inventory-management" element={<InventoryManagement />} /> */}
          <Route path="fixed-assets" element={<FixedAssets />} />
          <Route path="llama-ai" element={<LlamaAI />} />
          <Route path="tax" element={<Tax />} />
          <Route path="tax-calculation" element={<TaxCalculation />} />
          <Route path="clients" element={<Clients />} />
          <Route path="companies" element={<Companies />} />
          <Route path="security" element={<Security />} />
          <Route path="help" element={<Help />} />
          <Route path="help-support" element={<Help />} />
          <Route path="financial-reports" element={<FinancialReports />} />
          {/* Alias to support sidebar link `/dashboard/financial-reporting` */}
          <Route path="financial-reporting" element={<FinancialReports />} />
          <Route path="budget-management" element={<BudgetManagement />} />
          <Route path="documents" element={<Documents />} />
          <Route path="journal/new" element={<JournalNew />} />
          <Route path="custom-report-builder" element={<Reports />} />
          <Route path="analytics" element={<Analytics />} />
          {/* Add more routes as needed */}
        </Route>
      </Routes>
      <Toaster />
      <SonnerToaster position="top-right" />
      {/* <CookieConsent /> */}
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <I18nProvider defaultLanguage="en">
        <AuthProvider>
          <SidebarProvider>
            <AppContent />
            {/* Full-screen offline component with branding - takes over entire app */}
            <OfflineScreen showFullScreen={true} />
          </SidebarProvider>
        </AuthProvider>
      </I18nProvider>
    </ErrorBoundary>
  )
}

export default App
